import type {
  AddBrandInput,
  BrandListResponse,
  BrandSummary,
  ManualSaleInput,
  Sale,
  SaleListResponse,
  ScanResponse,
  ScanResult,
  UpdateBrandInput,
} from '../../shared/saleFinder';
import { readError } from './api';

export interface SaleFilterInput {
  status?: string;
  category?: string;
  tier?: string;
  country?: string;
}

export async function getSales(filters: SaleFilterInput = {}): Promise<Sale[]> {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.category) params.set('category', filters.category);
  if (filters.tier) params.set('tier', filters.tier);
  params.set('country', filters.country ?? 'IN');

  const response = await fetch(`/api/sales?${params.toString()}`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(await readError(response));
  return ((await response.json()) as SaleListResponse).sales;
}

export async function getBrands(): Promise<BrandSummary[]> {
  const response = await fetch('/api/brands', {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(await readError(response));
  return ((await response.json()) as BrandListResponse).brands;
}

// Scans a single brand. The caller loops over brands so each request stays
// well under Cloudflare's per-invocation subrequest limit.
export async function scanBrand(brandId: string, force = false): Promise<ScanResult> {
  const response = await fetch('/api/sales/scan', {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ brandId, force }),
  });
  if (!response.ok) throw new Error(await readError(response));
  const [result] = ((await response.json()) as ScanResponse).results;
  if (!result) throw new Error('The scan returned no result.');
  return result;
}

export async function addBrand(input: AddBrandInput): Promise<void> {
  const response = await fetch('/api/brands', {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw new Error(await readError(response));
}

export async function setBrandEnabled(brandId: string, enabled: boolean): Promise<void> {
  await updateBrand(brandId, { enabled });
}

export async function updateBrand(brandId: string, patch: UpdateBrandInput): Promise<void> {
  const response = await fetch(`/api/brands/${encodeURIComponent(brandId)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!response.ok) throw new Error(await readError(response));
}

export async function deleteBrand(brandId: string): Promise<void> {
  const response = await fetch(`/api/brands/${encodeURIComponent(brandId)}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(await readError(response));
}

export async function addManualSale(input: ManualSaleInput): Promise<{ status: string }> {
  const response = await fetch('/api/sales/manual', {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw new Error(await readError(response));
  return (await response.json()) as { status: string };
}
